// A map board on one canvas (overworld, local map): the char grid through a kit's view-tile
// ruleset (`paintTiled`, as PixelTerrain), then `fills` (ground swaps such as mud) and `props`
// (peaks, extra trees, planks, walls) straight from the world atlas, in row order. Drawn at the
// atlas's 24 px and scaled by CSS at a whole number with `image-rendering: pixelated` (16 §1).
// `ice` (0..1, docs/24 K6) freezes the water cells (`~`): a pale sheet with pixel cracks and glints.
// A second, transparent canvas over it moves the liquids (docs/28 L6, `mapLiquids.ts`): sparkles,
// lapping foam, bog bubbles, rings where `weather` rains on the water; still under reduce motion.
// Always the Oryx sheet: the custom overworld art (docs/70) has its own `CustomMapCanvas`.
import { useEffect, useMemo, useRef, useState } from 'react';
import { loadWorldAtlas, paintTiled, terrainFromRows } from '../../components';
import { kitFor, tileBoard } from '../../../game/tiling';
import { frame as frameName } from '../../../game/tiling/oryx';
import type { MapProp } from './overworld';
import { drawMapLiquids, mapLiquids, spawnRings, type MapLiquidState, type MapWeather } from './mapLiquids';
import { useSettings } from '../../../core/stores/settings';
import './map.css';

type Atlas = { img: CanvasImageSource; frames: Record<string, { x: number; y: number; w: number; h: number }> };
const NONE: readonly MapProp[] = [];

/** Ice over the water cells at strength `ice`: a sheet, a cold rim, and cracks seeded per cell. */
function freeze(ctx: CanvasRenderingContext2D, rows: readonly string[], ice: number) {
  const water = (x: number, y: number) => rows[y]?.[x] === '~';
  for (let y = 0; y < rows.length; y++) for (let x = 0; x < (rows[y]?.length ?? 0); x++) {
    if (!water(x, y)) continue;
    const px = x * 24, py = y * 24;
    ctx.fillStyle = `rgba(214, 232, 250, ${0.78 * ice})`;
    ctx.fillRect(px, py, 24, 24);
    // a whiter rim where the ice meets the bank
    ctx.fillStyle = `rgba(250, 252, 255, ${0.7 * ice})`;
    if (!water(x - 1, y)) ctx.fillRect(px, py, 2, 24);
    if (!water(x + 1, y)) ctx.fillRect(px + 22, py, 2, 24);
    // cracks and glints, the same every frame for a cell
    let h = (x * 73856093) ^ (y * 19349663);
    const r = () => { h = (h * 1103515245 + 12345) & 0x7fffffff; return h / 0x7fffffff; };
    ctx.fillStyle = `rgba(120, 160, 205, ${0.75 * ice})`;
    let cx = 3 + Math.floor(r() * 18), cy = Math.floor(r() * 6);
    for (let i = 0; i < 14 && cy < 24; i++) { ctx.fillRect(px + cx, py + cy, 1, 1); cy += 1 + (r() < 0.4 ? 1 : 0); cx = Math.max(2, Math.min(21, cx + (r() < 0.5 ? -1 : 1))); }
    ctx.fillStyle = `rgba(255, 255, 255, ${0.9 * ice})`;
    if (r() < 0.5) ctx.fillRect(px + 4 + Math.floor(r() * 16), py + 4 + Math.floor(r() * 16), 2, 1);
  }
}

export function MapCanvas({ rows, seed, kit, fills = NONE, props = NONE, scale = 2, animate, ice = 0, weather, cast, className = '' }: {
  rows: readonly string[]; seed: number; kit: string; fills?: readonly MapProp[]; props?: readonly MapProp[]; scale?: number; animate?: boolean; ice?: number;
  /** What falls on the water (rings, dimples) and which columns it is falling on now. */
  weather?: Omit<MapWeather, 'ice'>;
  /** The sun's shadows: trees and props cast their silhouette `dx`, `dy` atlas px away, at `alpha`; `flat` frames cast none. */
  cast?: { dx: number; dy: number; alpha: number; flat?: ReadonlySet<number> };
  className?: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const casters = useRef<HTMLCanvasElement>(undefined);
  const [atlas, setAtlas] = useState<Atlas>();
  const [phase, setPhase] = useState(0);
  const w = rows[0]?.length ?? 0, h = rows.length;
  const tiled = useMemo(() => tileBoard(terrainFromRows(rows, seed), kitFor(kit)), [rows, seed, kit]);
  useEffect(() => {
    let live = true;
    void loadWorldAtlas().then((a) => { if (live && a) setAtlas(a); });
    return () => { live = false; };
  }, []);
  useEffect(() => {
    if (!animate) return;
    const t = window.setInterval(() => setPhase((p) => (p + 1) % 2), 650);
    return () => window.clearInterval(t);
  }, [animate]);
  useEffect(() => {
    const ctx = ref.current?.getContext('2d');
    if (!ctx || !atlas) return;
    paintTiled(ctx, tiled, atlas, 24, phase, ['ground', 'overlay']);
    const drawOn = (c: CanvasRenderingContext2D) => (p: MapProp) => {
      const f = atlas.frames[frameName(p.frame)];
      if (f) c.drawImage(atlas.img, f.x, f.y, f.w, f.h, p.x * 24, p.y * 24 + (24 - f.h), f.w, f.h);
    };
    fills.forEach(drawOn(ctx));
    if (ice > 0) freeze(ctx, rows, ice);
    if (cast && cast.alpha > 0 && (cast.dx || cast.dy)) {
      // every standing thing's silhouette, filled dark, laid on the ground away from the sun
      const off = (casters.current ??= document.createElement('canvas'));
      off.width = w * 24; off.height = h * 24;
      const oc = off.getContext('2d');
      if (oc) {
        paintTiled(oc, tiled, atlas, 24, phase, ['props']);
        props.filter((p) => !cast.flat?.has(p.frame)).forEach(drawOn(oc));
        oc.globalCompositeOperation = 'source-in';
        oc.fillStyle = 'rgb(14, 16, 38)';
        oc.fillRect(0, 0, off.width, off.height);
        ctx.globalAlpha = cast.alpha;
        ctx.drawImage(off, cast.dx, cast.dy);
        ctx.globalAlpha = 1;
      }
    }
    paintTiled(ctx, tiled, atlas, 24, phase, ['props']);
    props.forEach(drawOn(ctx));
  }, [atlas, tiled, fills, props, phase, ice, rows, cast, w, h]);
  return (
    <>
      <canvas ref={ref} className={`fk-mapcanvas ${className}`} width={w * 24} height={h * 24} aria-hidden="true" data-ready={atlas ? 'true' : 'false'} style={{ width: w * 24 * scale, height: h * 24 * scale }} />
      <MapLiquids rows={rows} kit={kit} fills={fills} ice={ice} weather={weather} w={w} h={h} scale={scale} className={className} />
    </>
  );
}

/** The moving liquids over the terrain (docs/28 L6): ~12 frames a second, one still frame under reduce motion. */
function MapLiquids({ rows, kit, fills, ice, weather, w, h, scale, className }: { rows: readonly string[]; kit: string; fills: readonly MapProp[]; ice: number; weather?: Omit<MapWeather, 'ice'>; w: number; h: number; scale: number; className: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const still = useSettings((s) => s.reduceMotion);
  const cells = useMemo(() => mapLiquids(rows, kit, fills), [rows, kit, fills]);
  const wx = useRef<MapWeather>({ precip: undefined, amount: 0, ice });
  wx.current = { precip: weather?.precip, amount: weather?.amount ?? 0, under: weather?.under, ice };
  useEffect(() => {
    const ctx = ref.current?.getContext('2d');
    if (!ctx || cells.length === 0) return;
    const state: MapLiquidState = { rings: [], last: 0 };
    if (still) { drawMapLiquids(ctx, cells, state, wx.current, 12.5); return; }
    let raf = 0, last = -1;
    const frame = (ms: number) => {
      raf = requestAnimationFrame(frame);
      if (last >= 0 && ms - last < 80) return;
      last = ms;
      const t = ms / 1000;
      spawnRings(state, cells, wx.current, t, w);
      drawMapLiquids(ctx, cells, state, wx.current, t);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [cells, still, w]);
  if (cells.length === 0) return null;
  return <canvas ref={ref} className={`fk-mapcanvas fk-mapcanvas--liquid ${className}`} width={w * 24} height={h * 24} aria-hidden="true" data-testid="map-liquids" data-cells={cells.length} style={{ width: w * 24 * scale, height: h * 24 * scale }} />;
}
